import React, { useEffect, useRef, useState } from 'react'

import {
    useBetSliderStore,
    useFreeSpinsBonusesStore,
    useConfigurationStore,
    useCustomResultStore,
    useFreespinsStore,
    useJackpotStore,
    useNavigationStore,
    useSpinButtonSectionStore,
    useBuyBonusStore,
    useComponentStatesStore,
    useFreeBuyBonusStore,
    useLuckySpinsStore,
    useMenuStore,
    useCountdownFSBonusStore,
    useAmuseBoostStore,
    usePlayerInfoStore,
} from '@stores'
import {
    AutoplayButton,
    BetOptionButton,
    CollectButton,
    GambleButton,
    GambleSuitButton,
    SpeedButton,
    SpinButtonSVG,
    EQSpinButtonSVG,
    StopButton,
} from './'

import {
    useChangeTabVisibility,
    useCircleProgressAnimation,
    useLongPressAnimation,
    useOpacityToggle,
    usePointerOver,
} from '@hooks'
import { wait, withAutoBlur } from '../../utils/misc'
import styles from './SpinButtonSection.module.css'
import {
    ChangeOpacityComponents,
    CollectClickedMessage,
    EndTurboMessage,
    GameUIMessageScope,
    GameUIMessageType,
    SetIsBetOptionsButtonInteractiveMessage,
    SetIsSpinButtonInteractiveMessage,
    SetSpeedButtonDisabledMessage,
    SetSpeedButtonLevelMessage,
    ShowInfoLineTextMessage,
    SpinButtonSendIdleMessage,
    SpinButtonStartFreespinMessage,
    SpinButtonState,
    SpinButtonUpdateAutoplayCountMessage,
    SpinClickedMessage,
    StartTurboMessage,
    StopAutoplayClickedMessage,
    StopClickedMessage,
    ViewportType,
} from 'game-ui-types'
import { useTranslation } from '@amusnet/translations/react'
import { spinButtonTurboTextFontSizeMap } from './config'
import { cn } from '@amusnet/react-ui'
import { isBetOptionItem } from '@utils/betOption'

declare global {
    interface Window {
        useRedesignedGamble: string
    }
}

const SpinButtonSection: React.FC = () => {
    const { t } = useTranslation()
    const useRedesignedGamble = window.useRedesignedGamble

    // declare local state variables
    const [isSideButtonActive, setSideButtonActive] = useState(true)
    const [isSpinButtonInteractive, setIsSpinButtonInteractive] = useState(true)
    const [isBetOptionsButtonInteractive, setIsBetOptionsButtonInteractive] = useState(true)
    const [isSpeedButtonActive, setSpeedButtonActive] = useState(true)
    const [isTurboInProgress, setIsTurboInProgress] = useState(false)
    const [isRunning, setIsRunning] = useState(false)
    const [shouldHideSpinButtonText, setShouldHideSpinButtonText] = useState(false)

    // declare zustand store variables
    const viewport = useConfigurationStore((state) => state.viewport)
    const language = useConfigurationStore((state) => state.language)
    const shouldShowSpeedButton = useConfigurationStore((state) => state.shouldShowSpeedButton)
    const minimumSpinTimeDuration = useConfigurationStore((state) => state.minimumSpinTimeDuration)
    const shouldDisableTurbospin = useConfigurationStore((state) => state.shouldDisableTurbospin)
    const shouldDisableSpaceKey = useConfigurationStore((state) => state.shouldDisableSpaceKey)
    const isGameLoaded = useConfigurationStore((state) => state.isGameLoaded)
    const shouldBalanceUpdateImmediately = useConfigurationStore((state) => state.shouldBalanceUpdateImmediately)
    const shouldEnableUltraSpeed = useConfigurationStore((state) => state.shouldEnableUltraSpeed)
    const betOptions = useConfigurationStore((state) => state.betOptions)
    const getComponentConfig = useConfigurationStore((state) => state.getComponentConfig)
    const computedReelSizes = useConfigurationStore((state) => state.computedReelSizes)
    // TODO: remove when promo removed from platform
    const hasWidgetInUI = useConfigurationStore((state) => state.hasWidgetInUI)

    const spinButtonState = useSpinButtonSectionStore((state) => state.state)
    const autoplayCount = useSpinButtonSectionStore((state) => state.autoplayCount)
    const isAutoplayMode = useSpinButtonSectionStore((state) => state.isAutoplayMode)
    const isOverlayOpened = useSpinButtonSectionStore((state) => state.isOverlayOpened)
    const isLeftHandMode = useSpinButtonSectionStore((state) => state.isLeftHandMode)
    const close = useSpinButtonSectionStore((state) => state.close)
    const playPickersCloseSound = useSpinButtonSectionStore((state) => state.playPickersCloseSound)
    const setState = useSpinButtonSectionStore((state) => state.setState)
    const updateAutoplayCount = useSpinButtonSectionStore((state) => state.updateAutoplayCount)
    const updateAutoplayMode = useSpinButtonSectionStore((state) => state.updateAutoplayMode)
    const updateIsLongPress = useSpinButtonSectionStore((state) => state.updateIsLongPress)
    const updateIsBetInProgress = useSpinButtonSectionStore((state) => state.updateIsBetInProgress)
    const isBetInProgress = useSpinButtonSectionStore((state) => state.isBetInProgress)
    const isBetSectionOpened = useSpinButtonSectionStore((state) => state.isBetSectionOpened)
    const isAutoplaySectionOpened = useSpinButtonSectionStore((state) => state.isAutoplaySectionOpened)
    const isLongPress = useSpinButtonSectionStore((state) => state.isLongPress)
    const setIsSpaceKeyPressed = useSpinButtonSectionStore((state) => state.setIsSpaceKeyPressed)
    const isGambleActive = useSpinButtonSectionStore((state) => state.isGambleActive)
    const setIsGambleActive = useSpinButtonSectionStore((state) => state.setIsGambleActive)
    const speedButtonLevel = useSpinButtonSectionStore((state) => state.speedButtonLevel)
    const setSpeedButtonLevel = useSpinButtonSectionStore((state) => state.setSpeedButtonLevel)

    const setIsMenuDisabled = useNavigationStore((state) => state.setIsMenuDisabled)
    const setIsHomeDisabled = useNavigationStore((state) => state.setIsHomeDisabled)
    const isMenuOpen = useNavigationStore((state) => state.isMenuOpen)

    const setIsQuickSpinOn = useMenuStore((state) => state.setIsQuickSpinOn)

    const isCustomResultOpened = useCustomResultStore((state) => state.isOpened)

    const isJackpotEQRunning = useJackpotStore((state) => state.isJackpotEQStarted)
    const isEQJackpotType = useJackpotStore((state) => state.isEQJackpotType)

    const isScrolling = useBetSliderStore((state) => state.isScrolling)
    const betGroup = useBetSliderStore((state) => state.betGroup)
    const isEdgeReached = useBetSliderStore((state) => state.isEdgeReached)

    const isShowingFreespinsSplash = useFreespinsStore((state) => state.isShowingFreespinsSplash)
    const setIsShowingFreespinsSplash = useFreespinsStore((state) => state.setIsShowingFreespinsSplash)

    const showBuyBonusModal = useBuyBonusStore((state) => state.showModal)
    const isAmuseBoostModalOpen = useAmuseBoostStore((state) => state.isAmuseBoostModalOpen)
    const isExternalUIComponentActive = useComponentStatesStore((state) => state.isExternalUIComponentActive)
    const isFSBModalOpened = useFreeSpinsBonusesStore((state) => state.isModalOpened)
    const isFBBModalOpened = useFreeBuyBonusStore((state) => state.isModalOpened)
    const isLSModalOpened = useLuckySpinsStore((state) => state.isModalOpened)
    const areLSAboutToStart = useLuckySpinsStore((state) => state.areLSAboutToStart)
    const isCFSModalOpened = useCountdownFSBonusStore((state) => state.isModalOpened)
    const cfsActiveBonus = useCountdownFSBonusStore((state) => state.activeBonus)
    const hasActiveCFSBonusExpired = useCountdownFSBonusStore((state) => state.hasActiveBonusExpired)

    const spinButtonSectionConfig = getComponentConfig('SpinButtonSection')
    const spinButtonGambleSectionConfig = getComponentConfig('SpinButtonGambleSection')
    const spinButtonConfig = getComponentConfig('SpinButton')
    const speedButtonConfig = getComponentConfig('SpeedButton')
    const leftHandModeConfig = getComponentConfig('SpinButtonSectionLeftHand')
    const leftHandGambleModeConfig = getComponentConfig('SpinButtonGambleSectionLeftHand')

    const hasMinimumSpinTime = minimumSpinTimeDuration > 0
    const isPortraitGamble =
        viewport === ViewportType.MOBILE_PORTRAIT && spinButtonState === SpinButtonState.SEND_GAMBLE_STATE

    const hasOneBetOption = !betGroup && betOptions.length === 1

    // declare refs for various states
    const stateRef = useRef(spinButtonState)
    const isTurboRef = useRef(isTurboInProgress)
    const isShowingFreespinsSplashRef = useRef(isShowingFreespinsSplash)
    const spinButtonRef = useRef<HTMLButtonElement | null>(null)
    const spinButtonAnimationRef = useRef<SVGCircleElement | null>(null)
    const shouldDisableSpaceKeyRef = useRef(shouldDisableSpaceKey)
    const isScrollingRef = useRef(isScrolling)
    const isBetInProgressRef = useRef(isBetInProgress)
    const isPointerOver = usePointerOver(spinButtonRef)
    const isPointerOverRef = useRef(isPointerOver)
    const isLongPressRef = useRef(isLongPress)
    const shouldIgnoreSpaceUpRef = useRef(false)
    const shouldIgnoreSpaceUpAfterInterruptedSpin = useRef(false)
    const shouldIgnoreMouseUpRef = useRef(false)
    const isInterruptedByRightClick = useRef(false)
    const spinButtonSectionComponent = useRef<HTMLDivElement | null>(null)
    const speedButtonComponent = useRef<HTMLDivElement | null>(null)
    const isBetTriggeredFromSpaceRef = useRef(false)
    const isBetTriggeredFromMouseRef = useRef(false)
    const isMenuOpenRef = useRef(isMenuOpen)
    const isBonusModalOpenedRef = useRef(
        showBuyBonusModal ||
            isCFSModalOpened ||
            isFBBModalOpened ||
            isFSBModalOpened ||
            isLSModalOpened ||
            isAmuseBoostModalOpen
    )
    const spinStartTimeRef = useRef<number>(0)

    const isSpinButtonInteractiveRef = useRef(isSpinButtonInteractive)
    const [isLongPressEnabled, setIsLongPressEnabled] = useState(false)

    const spinButtonPosition =
        isGambleActive && useRedesignedGamble
            ? { ...(isLeftHandMode ? leftHandGambleModeConfig.position : spinButtonGambleSectionConfig.position) }
            : {
                  ...(isLeftHandMode
                      ? leftHandModeConfig.position
                      : isJackpotEQRunning
                        ? spinButtonSectionConfig.positionEQ
                        : spinButtonSectionConfig.position),
              }

    // TODO: remove hasWidgetInUI check once promo removed from platform
    if ((hasWidgetInUI && viewport === ViewportType.DESKTOP) || viewport === ViewportType.MOBILE_LANDSCAPE) {
        const reelSizes = computedReelSizes()[viewport]
        const remainingSpace = reelSizes.x

        const spinSectionWidth = parseInt(spinButtonSectionConfig.dimensions?.width ?? '0', 10)

        const position = Math.max(remainingSpace / 2 - spinSectionWidth / 2, 0)

        if (isLeftHandMode) {
            spinButtonPosition!.left = `${position}px`
            spinButtonPosition!.right = undefined
        } else {
            spinButtonPosition!.left = undefined
            spinButtonPosition!.right = `${position}px`
        }
    }

    const { isComponentInteractive: isSpinButtonSectionComponentInteractive } = useOpacityToggle(
        ChangeOpacityComponents.SPIN_BUTTON_SECTION,
        spinButtonSectionComponent
    )

    const { isComponentInteractive: isSpinButtonComponentInteractive } = useOpacityToggle(
        ChangeOpacityComponents.SPIN_BUTTON,
        spinButtonRef
    )

    const spinButtonFontSize = spinButtonTurboTextFontSizeMap[language] || 20

    useOpacityToggle(ChangeOpacityComponents.SPEED_BUTTON, speedButtonComponent)

    const showInsufficientBalance = () => {
        const showInsufficientBalanceMessage: ShowInfoLineTextMessage = {
            scope: GameUIMessageScope.INFO_LINE,
            type: GameUIMessageType.SHOW_INFO_LINE_TEXT,
            text: 'common.insufficientBalance',
        }

        window.postMessage(showInsufficientBalanceMessage, '*')
    }

    const hasEnoughDisplayedBalanceForBet = () => {
        if (!useConfigurationStore.getState().shouldBalanceSubtractOnly) {
            return true
        }

        const { balance, subtractedBalance } = usePlayerInfoStore.getState()
        const displayedBalance = subtractedBalance ?? balance
        const engineBetAmount = window.bottle?.container?.MainModel?.totalBet?.()

        if (typeof engineBetAmount === 'number' && engineBetAmount > 0) {
            return displayedBalance >= engineBetAmount
        }

        const { betOptions: currentBetOptions } = useConfigurationStore.getState()
        const { activeIndex, getActiveBetOption } = useBetSliderStore.getState()
        const { currentFeatureMultiplier } = useBuyBonusStore.getState()
        const activeBetOption = currentBetOptions[activeIndex]

        if (activeBetOption === undefined) {
            return false
        }

        const betAmount =
            (isBetOptionItem(activeBetOption) ? (getActiveBetOption()?.betAmount ?? 0) : Number(activeBetOption)) *
            currentFeatureMultiplier

        return betAmount > 0 && displayedBalance >= betAmount
    }

    const resetBlockedBetState = () => {
        updateIsBetInProgress(false)
        setSideButtonActive(true)
        setSpeedButtonActive(true)
        setIsBetOptionsButtonInteractive(true)
        setIsMenuDisabled(false)
        setIsHomeDisabled(false)
    }

    const postSpinClickedIfBalanceAllows = () => {
        if (!hasEnoughDisplayedBalanceForBet()) {
            showInsufficientBalance()
            resetBlockedBetState()

            return false
        }

        const spinClickedMessage: SpinClickedMessage = {
            type: GameUIMessageType.SPIN_CLICKED,
        }

        window.postMessage(spinClickedMessage, '*')

        return true
    }

    const handleSpinButtonClicked = () => {
        if (isInterruptedByRightClick.current) {
            isInterruptedByRightClick.current = false

            return
        }

        if (shouldIgnoreMouseUpRef.current) {
            shouldIgnoreMouseUpRef.current = false

            return
        }

        if (
            !isSpinButtonInteractiveRef.current ||
            isLongPressRef.current ||
            isTurboRef.current ||
            isMenuOpenRef.current ||
            isShowingFreespinsSplashRef.current ||
            isScrollingRef.current
        ) {
            if (
                stateRef.current === SpinButtonState.SEND_BEFORE_GAMBLE_STATE ||
                stateRef.current === SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE
            ) {
                const collectClickedMessage: CollectClickedMessage = { type: GameUIMessageType.COLLECT_CLICKED }

                window.postMessage(collectClickedMessage, '*')
                setSideButtonActive(false)
            }

            return
        }

        isBetTriggeredFromSpaceRef.current = false
        updateIsBetInProgress(true)
        close()

        switch (stateRef.current) {
            case SpinButtonState.SEND_IDLE_STATE: {
                if (postSpinClickedIfBalanceAllows()) {
                    setSideButtonActive(true)
                }

                break
            }
            case SpinButtonState.SEND_STOP_STATE:
            case SpinButtonState.SEND_STOP_STATE_WITH_WIN:
                if (isAutoplayMode) {
                    const stopAutoplayClickedMessage: StopAutoplayClickedMessage = {
                        type: GameUIMessageType.STOP_AUTOPLAY_CLICKED,
                    }

                    window.postMessage(stopAutoplayClickedMessage, '*')

                    updateAutoplayMode(false)
                    updateAutoplayCount(0)
                } else {
                    const stopClickedMessage: StopClickedMessage = { type: GameUIMessageType.STOP_CLICKED }

                    window.postMessage(stopClickedMessage, '*')
                }

                setSideButtonActive(false)
                break
            case SpinButtonState.SEND_COLLECT_STATE: {
                const collectClickedMessage: CollectClickedMessage = { type: GameUIMessageType.COLLECT_CLICKED }

                window.postMessage(collectClickedMessage, '*')
                setSideButtonActive(false)

                break
            }
            case SpinButtonState.SEND_BEFORE_GAMBLE_STATE:
                // If gamble is active, don't allow to spin
                if (useSpinButtonSectionStore.getState().isGambleActive) {
                    return
                }

                if (!shouldIgnoreSpaceUpAfterInterruptedSpin.current) {
                    if (postSpinClickedIfBalanceAllows()) {
                        setSideButtonActive(true)
                    }
                }

                shouldIgnoreSpaceUpAfterInterruptedSpin.current = false
                break
            case SpinButtonState.SEND_WIN_WITHOUT_GAMBLE_STATE: {
                if (postSpinClickedIfBalanceAllows()) {
                    setSideButtonActive(true)
                }

                break
            }
            case SpinButtonState.SEND_GAMBLE_STATE: {
                const collectClickedMessage: CollectClickedMessage = {
                    type: GameUIMessageType.COLLECT_CLICKED,
                    payload: { gambleWinCollect: true },
                }

                window.postMessage(collectClickedMessage, '*')
                setSideButtonActive(true)
                break
            }
            case SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE: {
                if (postSpinClickedIfBalanceAllows()) {
                    setSideButtonActive(true)
                }

                break
            }
            case SpinButtonState.SEND_WAITING_FOR_FREESPIN_ANIMATION_START_STATE: {
                const spinButtonStartFreespinMessage: SpinButtonStartFreespinMessage = {
                    type: GameUIMessageType.SPIN_BUTTON_START_FREESPIN,
                }

                window.postMessage(spinButtonStartFreespinMessage, '*')
                break
            }
            default:
                break
        }
    }

    const longPressAnimation = useLongPressAnimation({
        ref: spinButtonRef,
        pressDelay: 0.15,
        scale: 1.15,
        keyCode: 'Space',
        isSpaceKeyDisabled: shouldDisableSpaceKey,
        isEnabled: isLongPressEnabled,
        onPressStart: () => {
            if (isMenuOpenRef.current || isScrollingRef.current || isBonusModalOpenedRef.current) return

            if (
                stateRef.current !== SpinButtonState.SEND_IDLE_STATE &&
                stateRef.current !== SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE &&
                stateRef.current !== SpinButtonState.SEND_BEFORE_GAMBLE_STATE
            ) {
                longPressAnimation.kill()
                longPressAnimation.enable()
            } else {
                updateIsLongPress(true)
                isLongPressRef.current = true
                close()

                updateIsBetInProgress(false)
                isBetInProgressRef.current = false
                setSideButtonActive(false)
            }
        },
        onPressComplete: (isFromMouse: boolean) => {
            if (cfsActiveBonus && hasActiveCFSBonusExpired()) {
                // Interrupt turbo and reset state
                updateIsLongPress(false)
                isLongPressRef.current = false
                setIsTurboInProgress(false)
                setSpeedButtonActive(true)
                setSideButtonActive(
                    stateRef.current === SpinButtonState.SEND_IDLE_STATE ||
                        stateRef.current === SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE ||
                        stateRef.current === SpinButtonState.SEND_BEFORE_GAMBLE_STATE ||
                        stateRef.current === SpinButtonState.SEND_GAMBLE_STATE
                )

                return
            }

            if (isMenuOpenRef.current || isScrollingRef.current || isBonusModalOpenedRef.current) return

            if (
                stateRef.current === SpinButtonState.SEND_IDLE_STATE ||
                stateRef.current === SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE ||
                stateRef.current === SpinButtonState.SEND_BEFORE_GAMBLE_STATE
            ) {
                if (!hasEnoughDisplayedBalanceForBet()) {
                    showInsufficientBalance()
                    resetBlockedBetState()
                    updateIsLongPress(false)
                    isLongPressRef.current = false

                    return
                }

                setIsTurboInProgress(true)
                setSpeedButtonActive(false)

                isBetTriggeredFromMouseRef.current = isFromMouse

                const startTurboMessage: StartTurboMessage = { type: GameUIMessageType.START_TURBO }

                window.postMessage(startTurboMessage, '*')
            }
        },
        onRelease: () => {
            if (isMenuOpenRef.current || isScrollingRef.current || isBonusModalOpenedRef.current) return

            updateIsLongPress(false)
            setIsTurboInProgress(false)
            isLongPressRef.current = false

            setSideButtonActive(
                stateRef.current === SpinButtonState.SEND_IDLE_STATE ||
                    stateRef.current === SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE ||
                    stateRef.current === SpinButtonState.SEND_BEFORE_GAMBLE_STATE ||
                    stateRef.current === SpinButtonState.SEND_GAMBLE_STATE
            )

            if (!isTurboInProgress && stateRef.current === SpinButtonState.SEND_TURBO_STATE) {
                setIsMenuDisabled(false)
                setIsHomeDisabled(false)
            }

            if (isTurboRef.current) {
                const endTurboMessage: EndTurboMessage = { type: GameUIMessageType.END_TURBO }

                window.postMessage(endTurboMessage, '*')
            }

            if (
                stateRef.current === SpinButtonState.SEND_IDLE_STATE ||
                stateRef.current === SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE ||
                stateRef.current === SpinButtonState.SEND_BEFORE_GAMBLE_STATE
            ) {
                if (isBetInProgressRef.current || (!isPointerOverRef.current && !isBetTriggeredFromSpaceRef.current)) {
                    updateIsBetInProgress(false)

                    return
                }

                close()

                handleSpinButtonClicked()

                isBetInProgressRef.current = true
            }
        },
    })

    const isExternalUIComponentActiveRef = useRef(isExternalUIComponentActive)

    useEffect(() => {
        isExternalUIComponentActiveRef.current = isExternalUIComponentActive
    }, [isExternalUIComponentActive])

    useEffect(() => {
        isShowingFreespinsSplashRef.current = isShowingFreespinsSplash
    }, [isShowingFreespinsSplash])

    useEffect(() => {
        isSpinButtonInteractiveRef.current = isSpinButtonInteractive

        if (spinButtonRef.current) spinButtonRef.current.inert = !isSpinButtonInteractive
    }, [isSpinButtonInteractive])

    useEffect(() => {
        isBonusModalOpenedRef.current =
            showBuyBonusModal ||
            isCFSModalOpened ||
            isFBBModalOpened ||
            isFSBModalOpened ||
            isLSModalOpened ||
            isAmuseBoostModalOpen
    }, [
        showBuyBonusModal,
        isCFSModalOpened,
        isFBBModalOpened,
        isFSBModalOpened,
        isLSModalOpened,
        isAmuseBoostModalOpen,
    ])

    useEffect(() => {
        longPressAnimation.kill()

        isMenuOpenRef.current = isMenuOpen
    }, [isMenuOpen])

    useEffect(() => {
        setIsLongPressEnabled(
            isGameLoaded &&
                isExternalUIComponentActiveRef.current &&
                isSpinButtonSectionComponentInteractive &&
                isSpinButtonComponentInteractive &&
                !shouldDisableTurbospin &&
                !hasMinimumSpinTime &&
                !isMenuOpen &&
                isSpinButtonInteractiveRef.current &&
                !isShowingFreespinsSplashRef.current &&
                !isBonusModalOpenedRef.current
        )
    }, [
        isGameLoaded,
        isExternalUIComponentActive,
        isSpinButtonSectionComponentInteractive,
        isSpinButtonComponentInteractive,
        shouldDisableTurbospin,
        hasMinimumSpinTime,
        isMenuOpen,
        isSpinButtonInteractive,
        isShowingFreespinsSplash,
        showBuyBonusModal,
        isAmuseBoostModalOpen,
        isCFSModalOpened,
        isFBBModalOpened,
        isFSBModalOpened,
        isLSModalOpened,
    ])

    useChangeTabVisibility({
        onTabHidden: () => {
            if (isTurboRef.current) {
                setSpeedButtonActive(true)
                setIsTurboInProgress(false)
                updateIsLongPress(false)

                longPressAnimation.kill()

                const endTurboMessage: EndTurboMessage = { type: GameUIMessageType.END_TURBO }

                window.postMessage(endTurboMessage, '*')
            } else if (isLongPressRef.current) {
                // If long press animation had been started before tab switch
                // Stop it but reset on tab refocus
                longPressAnimation.kill()
                longPressAnimation.disable()
            }
        },
        onTabVisible: () => {
            if (isLongPressRef.current) {
                longPressAnimation.enable()
                updateIsLongPress(false)
            }
        },
    })

    useEffect(() => {
        const handlePointerUp = () => {
            shouldIgnoreMouseUpRef.current = false
        }

        window.addEventListener('pointerup', handlePointerUp)

        return () => {
            window.removeEventListener('pointerup', handlePointerUp)
        }
    }, [])

    useEffect(() => {
        stateRef.current = spinButtonState
    }, [spinButtonState])

    useEffect(() => {
        isScrollingRef.current = isScrolling

        if (isScrolling) {
            longPressAnimation.disable()
        } else {
            longPressAnimation.enable()
        }
    }, [isScrolling])

    useEffect(() => {
        isLongPressRef.current = isLongPress
    }, [isLongPress])

    useEffect(() => {
        isPointerOverRef.current = isPointerOver
    }, [isPointerOver])

    useEffect(() => {
        isBetInProgressRef.current = isBetInProgress
    }, [isBetInProgress])

    useEffect(() => {
        shouldDisableSpaceKeyRef.current = shouldDisableSpaceKey
    }, [shouldDisableSpaceKey])

    useEffect(() => {
        if (isTurboRef.current) {
            longPressAnimation.kill()
            setSpeedButtonActive(true)
            setIsTurboInProgress(false)
            updateIsLongPress(false)

            const endTurboMessage: EndTurboMessage = { type: GameUIMessageType.END_TURBO }

            window.postMessage(endTurboMessage, '*')
        }
    }, [viewport])

    useEffect(() => {
        isTurboRef.current = isTurboInProgress
    }, [isTurboInProgress])

    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            const rawData = event.data
            const { type } = rawData

            switch (type) {
                case GameUIMessageType.SPIN_STARTED: {
                    setIsRunning(true)

                    break
                }
                case GameUIMessageType.SPIN_BUTTON_SEND_COLLECT_STATE:
                    if (autoplayCount === 0) {
                        updateAutoplayMode(false)
                    }

                    setSideButtonActive(false)
                    setState(SpinButtonState.SEND_COLLECT_STATE)
                    break
                case GameUIMessageType.SPIN_BUTTON_SEND_IDLE_STATE: {
                    if (autoplayCount === 0) {
                        updateAutoplayMode(false)
                    }

                    setSideButtonActive(true)
                    setIsGambleActive(false)

                    const data = rawData as SpinButtonSendIdleMessage

                    setShouldHideSpinButtonText(data.shouldHideSpinButtonText ?? false)
                    setState(SpinButtonState.SEND_IDLE_STATE)
                    break
                }
                case GameUIMessageType.SPIN_BUTTON_SEND_STOP_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_STOP_STATE_WITH_WIN:
                    setSideButtonActive(false)

                    if (!isTurboRef.current) {
                        setState(SpinButtonState.SEND_STOP_STATE)
                    }

                    break
                case GameUIMessageType.SPIN_BUTTON_SEND_BEFORE_GAMBLE_STATE:
                    setSideButtonActive(true)

                    setState(SpinButtonState.SEND_BEFORE_GAMBLE_STATE)

                    if (shouldBalanceUpdateImmediately) {
                        window.postMessage({ type: GameUIMessageType.COLLECT_CLICKED }, '*')
                    }

                    break
                case GameUIMessageType.SPIN_BUTTON_SEND_WIN_WITHOUT_GAMBLE_STATE:
                    setSideButtonActive(true)

                    setState(SpinButtonState.SEND_WIN_WITHOUT_GAMBLE_STATE)

                    if (shouldBalanceUpdateImmediately) {
                        window.postMessage({ type: GameUIMessageType.COLLECT_CLICKED }, '*')
                    }

                    break
                case GameUIMessageType.SPIN_BUTTON_UPDATE_AUTOPLAY_COUNT: {
                    setState(SpinButtonState.UPDATE_AUTOPLAY_COUNT)

                    const data = rawData as SpinButtonUpdateAutoplayCountMessage

                    if (data.payload !== 0) {
                        updateAutoplayMode(true)
                    }

                    if (autoplayCount === -1) {
                        return
                    }

                    if (data.payload === 0) {
                        spinStartTimeRef.current = Date.now()
                    }

                    updateAutoplayCount(data.payload)
                    break
                }
                case GameUIMessageType.SPIN_BUTTON_SEND_GAMBLE_STATE:
                    setSideButtonActive(true)
                    setIsGambleActive(true)
                    setState(SpinButtonState.SEND_GAMBLE_STATE)

                    break

                case GameUIMessageType.SPIN_BUTTON_SEND_HIDE_STATE:
                    setSideButtonActive(false)
                    updateIsLongPress(false)
                    setState(SpinButtonState.SEND_HIDE_STATE)
                    break

                case GameUIMessageType.SPIN_BUTTON_SEND_TURBO_STATE:
                    setSideButtonActive(false)
                    updateIsLongPress(false)
                    setState(SpinButtonState.SEND_TURBO_STATE)
                    break

                case GameUIMessageType.SPIN_BUTTON_SEND_IDLE_WITHOUT_GAMBLE_STATE:
                    setSideButtonActive(true)
                    setState(SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE)

                    if (shouldBalanceUpdateImmediately) {
                        window.postMessage({ type: GameUIMessageType.COLLECT_CLICKED }, '*')
                    }

                    break

                case GameUIMessageType.SPIN_BUTTON_AUTOPLAY_STOPPED: {
                    const elapsed = Date.now() - spinStartTimeRef.current
                    const delay = minimumSpinTimeDuration * 1000 - elapsed

                    if (delay > 0) {
                        await wait(delay)
                    }

                    updateAutoplayMode(false)
                    updateAutoplayCount(0)
                    break
                }
                case GameUIMessageType.SPIN_BUTTON_SEND_WAITING_FOR_FREESPIN_ANIMATION_START_STATE:
                    // longPressAnimation.disable()
                    setSideButtonActive(false)
                    updateIsLongPress(false)
                    setState(SpinButtonState.SEND_WAITING_FOR_FREESPIN_ANIMATION_START_STATE)
                    break
                case GameUIMessageType.SET_SPEED_BUTTON_LEVEL: {
                    const data = rawData as SetSpeedButtonLevelMessage

                    setSpeedButtonLevel(data.speedLevel)

                    setIsQuickSpinOn(data.speedLevel >= (shouldEnableUltraSpeed ? 3 : 2))

                    break
                }
                case GameUIMessageType.SET_SPEED_BUTTON_DISABLED: {
                    const data = rawData as SetSpeedButtonDisabledMessage

                    setSpeedButtonActive(!data.isDisabled)
                    break
                }
                case GameUIMessageType.SET_IS_BET_OPTIONS_BUTTON_INTERACTIVE: {
                    const data = rawData as SetIsBetOptionsButtonInteractiveMessage

                    setIsBetOptionsButtonInteractive(data.isInteractive)
                    break
                }
                case GameUIMessageType.FORCE_STOP_TURBO_SPIN: {
                    shouldIgnoreSpaceUpRef.current = true
                    shouldIgnoreSpaceUpAfterInterruptedSpin.current = true

                    setIsTurboInProgress(false)
                    updateIsLongPress(false)
                    setSideButtonActive(true)
                    break
                }
                case GameUIMessageType.FULLSCREEN_CHANGED:
                    if (isTurboRef.current) {
                        longPressAnimation.kill()

                        setIsTurboInProgress(false)
                        updateIsLongPress(false)
                        setSideButtonActive(true)

                        const endTurboMessage: EndTurboMessage = { type: GameUIMessageType.END_TURBO }

                        window.postMessage(endTurboMessage, '*')
                    }

                    break
                case GameUIMessageType.SET_IS_SPIN_BUTTON_INTERACTIVE: {
                    const data = rawData as SetIsSpinButtonInteractiveMessage

                    setIsSpinButtonInteractive(data.isInteractive)
                    break
                }
                case GameUIMessageType.FREESPINS_SPLASH_STARTED:
                    shouldIgnoreSpaceUpRef.current = false
                    setIsShowingFreespinsSplash(true)
                    break
                case GameUIMessageType.FREESPINS_SPLASH_ENDED:
                    setIsShowingFreespinsSplash(false)
                    break
                default:
                    break
            }
        }

        window.addEventListener('message', handleMessage)

        return () => {
            window.removeEventListener('message', handleMessage)
        }
    }, [
        setState,
        updateAutoplayCount,
        updateAutoplayMode,
        isSideButtonActive,
        isGambleActive,
        autoplayCount,
        shouldEnableUltraSpeed,
    ])

    const handleStopButtonClicked = () => {
        if (hasMinimumSpinTime) return

        const stopClickedMessage: StopClickedMessage = { type: GameUIMessageType.STOP_CLICKED }

        window.postMessage(stopClickedMessage, '*')
    }

    const updateSpinButtonSrc = () => {
        if (spinButtonState === SpinButtonState.SEND_COLLECT_STATE) {
            return !isEQJackpotType ? styles.spinButtonCollect : styles.spinButtonCollectEQ
        }

        if (spinButtonState === SpinButtonState.SEND_STOP_STATE) {
            return !isEQJackpotType ? styles.spinButtonStop : styles.spinButtonStopEQ
        }

        if (spinButtonState === SpinButtonState.SEND_STOP_STATE_WITH_WIN) {
            return !isEQJackpotType ? styles.spinButtonStop : styles.spinButtonStopEQ
        }

        if (spinButtonState === SpinButtonState.SEND_WIN_WITHOUT_GAMBLE_STATE) {
            return styles.spinButtonGamble
        }

        if (spinButtonState === SpinButtonState.UPDATE_AUTOPLAY_COUNT) {
            return !isEQJackpotType ? styles.spinButtonStop : styles.spinButtonStopEQ
        }

        if (spinButtonState === SpinButtonState.SEND_GAMBLE_STATE) {
            return !isEQJackpotType ? styles.spinButtonCollect : styles.spinButtonCollectEQ
        }
    }

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.code !== 'Space') return

            if (shouldDisableSpaceKeyRef.current) return

            if (!isExternalUIComponentActiveRef.current) return

            setIsSpaceKeyPressed?.(true)

            if (!isGameLoaded) return

            if (isScrollingRef.current) return

            if (isBonusModalOpenedRef.current) return

            if (isInterruptedByRightClick.current) return

            isBetTriggeredFromSpaceRef.current = true

            spinButtonRef.current?.blur()

            updateIsBetInProgress(false)

            if (!isMenuOpenRef.current) {
                setIsMenuDisabled(true)
                setIsHomeDisabled(true)

                setIsBetOptionsButtonInteractive(false)
                setSideButtonActive(false)
            }
        }
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.code === 'Space') {
                if (shouldDisableSpaceKeyRef.current) return

                if (!isGameLoaded) return

                if (!isExternalUIComponentActiveRef.current) return

                setIsSpaceKeyPressed?.(false)

                const activeElement = document.activeElement as HTMLElement

                if (
                    activeElement &&
                    (activeElement.id === 'gamble-red' ||
                        activeElement.id === 'gamble-black' ||
                        activeElement.id === 'gamble-button')
                ) {
                    //if "space" key is pressed with other than spin button simultaneously, we need to prevent default action
                    e.preventDefault()
                    activeElement.blur()
                    setSideButtonActive(true)

                    return
                }

                e.preventDefault()

                if (shouldIgnoreSpaceUpRef.current && !isBetTriggeredFromMouseRef.current) {
                    shouldIgnoreSpaceUpRef.current = false
                    setSideButtonActive(stateRef.current !== SpinButtonState.SEND_COLLECT_STATE)

                    return
                }

                if (
                    !isTurboRef.current &&
                    !isMenuOpenRef.current &&
                    !isLongPressRef.current &&
                    !isScrollingRef.current &&
                    !isShowingFreespinsSplashRef.current &&
                    !isBonusModalOpenedRef.current
                ) {
                    close()
                    updateIsBetInProgress(true)
                    handleSpinButtonClicked()
                    updateIsLongPress(false)
                }
            }
        }

        const handleRightClick = (e: MouseEvent) => {
            const isRightMouseClick = e.button === 2

            if (isRightMouseClick) {
                if (isTurboRef.current || isLongPressRef.current) {
                    longPressAnimation.kill()
                    longPressAnimation.enable()
                    setIsTurboInProgress(false)
                    updateIsLongPress(false)
                    isInterruptedByRightClick.current = true
                }

                if (isTurboRef.current) {
                    const endTurboMessage: EndTurboMessage = { type: GameUIMessageType.END_TURBO }

                    window.postMessage(endTurboMessage, '*')
                }
            }
        }

        const handleWindowBlur = () => {
            // Keyup is delivered to the focused document; leaving this window (e.g. parent page
            // while the game runs in an iframe) skips keyup, so clear space-pressed UI state.
            setIsSpaceKeyPressed?.(false)
        }

        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('keyup', handleKeyUp)
        window.addEventListener('blur', handleWindowBlur)
        window.addEventListener('contextmenu', handleRightClick)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('keyup', handleKeyUp)
            window.removeEventListener('blur', handleWindowBlur)
            window.removeEventListener('contextmenu', handleRightClick)
        }
    }, [isGameLoaded])

    useEffect(() => {
        const handleContextMenu = (e: MouseEvent) => {
            e.preventDefault()
        }

        const element = spinButtonRef.current

        if (element) {
            element.addEventListener('contextmenu', handleContextMenu)
        }

        return () => {
            if (element) {
                element.removeEventListener('contextmenu', handleContextMenu)
            }
        }
    }, [])

    const renderSpinButton = () => {
        const SpinButtonSVGComponent = !isEQJackpotType ? SpinButtonSVG : EQSpinButtonSVG

        return (
            <button
                id="spin-button"
                ref={spinButtonRef}
                className={cn(
                    'w-[200px] h-[200px] relative focus:outline-none flex z-[50] rounded-full overflow-hidden',
                    spinButtonConfig.styles
                )}
                onClick={(e) => {
                    if (isInterruptedByRightClick.current) return

                    withAutoBlur(handleSpinButtonClicked)(e)
                }}
                onMouseDown={(e) => {
                    if (e.button === 2) return // ignore right-click

                    isInterruptedByRightClick.current = false
                }}
            >
                {spinButtonState === SpinButtonState.SEND_IDLE_STATE ||
                spinButtonState === SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE ||
                spinButtonState === SpinButtonState.SEND_TURBO_STATE ||
                spinButtonState === SpinButtonState.SEND_BEFORE_GAMBLE_STATE ? (
                    <SpinButtonSVGComponent
                        text={
                            hasMinimumSpinTime ||
                            shouldDisableTurbospin ||
                            isTurboInProgress ||
                            shouldHideSpinButtonText
                                ? ''
                                : t('gameui.holdForTurboSpinLabel')
                        }
                        fontSize={spinButtonFontSize}
                    />
                ) : (
                    <img
                        id={updateSpinButtonSrc()}
                        alt="Spin Button"
                        className={cn(updateSpinButtonSrc(), 'w-full h-full')}
                    />
                )}
                {hasMinimumSpinTime && (
                    <svg
                        width="100%"
                        height="100%"
                        style={{ position: 'absolute', visibility: isRunning ? 'visible' : 'hidden' }}
                    >
                        <circle
                            ref={spinButtonAnimationRef}
                            cx="100"
                            cy="100"
                            r="97"
                            stroke="#949494"
                            strokeWidth="7"
                            opacity={0.7}
                            fill="none"
                            strokeLinecap="round"
                            transform="rotate(-90, 100, 100)"
                        />
                    </svg>
                )}

                {isAutoplayMode && (
                    <div
                        id="autoplay-count"
                        className="text-[40px] text-black font-bold"
                        style={{
                            display: 'flex',
                            transform: 'translate(-50%, -50%)',
                            left: '50%',
                            top: '50%',
                            alignSelf: 'center',
                            position: 'absolute',
                            visibility: spinButtonState === SpinButtonState.SEND_COLLECT_STATE ? 'hidden' : 'visible',
                        }}
                    >
                        {autoplayCount === -1 ? '∞' : autoplayCount}
                    </div>
                )}
            </button>
        )
    }

    const renderSpeedButton = () => {
        if (!shouldShowSpeedButton) return null

        return (
            <div
                ref={speedButtonComponent}
                className={cn('rounded-[25px] overflow-hidden', speedButtonConfig.styles)}
                style={{
                    ...speedButtonConfig.position,
                }}
            >
                <SpeedButton
                    isActive={isSpeedButtonActive && !isLongPress}
                    speedLevel={speedButtonLevel}
                    isUltraPlayButtonActive={!isScrolling && isSideButtonActive}
                />
            </div>
        )
    }

    const renderAllButtons = () => {
        if (spinButtonState === SpinButtonState.SEND_GAMBLE_STATE) {
            return [
                <GambleSuitButton
                    key="gamble-red"
                    isActive={isSideButtonActive}
                    suitColor="red"
                    shouldUpdateOpacityWhenInactive={false}
                    disableAutoBlur={true}
                />,
                renderSpinButton(),
                <GambleSuitButton
                    key="gamble-black"
                    isActive={isSideButtonActive}
                    suitColor="black"
                    shouldUpdateOpacityWhenInactive={false}
                    disableAutoBlur={true}
                />,
            ]
        }

        // shouldBalanceUpdateImmediately = expo tournament and finnish requirement - prevent subtracting win amount from balance
        if (spinButtonState === SpinButtonState.SEND_BEFORE_GAMBLE_STATE) {
            return [
                <GambleButton isActive={isSideButtonActive} key="gamble" shouldUpdateOpacityWhenInactive={false} />,
                renderSpinButton(),
                renderSpeedButton(),
                shouldBalanceUpdateImmediately ? (
                    <BetOptionButton
                        key="betOption"
                        isActive={!hasOneBetOption && !isScrolling && isSideButtonActive}
                        isInteractive={isBetOptionsButtonInteractive}
                        shouldUpdateOpacityWhenInactive={
                            hasOneBetOption || stateRef.current !== SpinButtonState.SEND_IDLE_STATE
                        }
                    />
                ) : (
                    <CollectButton
                        isActive={isSideButtonActive}
                        key="collect"
                        shouldUpdateOpacityWhenInactive={false}
                    />
                ),
            ]
        }

        if (spinButtonState === SpinButtonState.SEND_IDLE_WITHOUT_GAMBLE_STATE) {
            return [
                <AutoplayButton
                    key="autoplay"
                    isActive={!isScrolling && isSideButtonActive}
                    shouldUpdateOpacityWhenInactive={false}
                    shouldActAsCollect={areLSAboutToStart}
                />,
                renderSpinButton(),
                renderSpeedButton(),
                shouldBalanceUpdateImmediately ? (
                    <BetOptionButton
                        key="betOption"
                        isActive={!hasOneBetOption && !isScrolling && isSideButtonActive}
                        isInteractive={isBetOptionsButtonInteractive}
                        shouldUpdateOpacityWhenInactive={
                            hasOneBetOption || stateRef.current !== SpinButtonState.SEND_IDLE_STATE
                        }
                    />
                ) : (
                    <CollectButton
                        isActive={!isScrolling && isSideButtonActive}
                        key="collect"
                        shouldUpdateOpacityWhenInactive={false}
                    />
                ),
            ]
        }

        return [
            <AutoplayButton
                key="autoplay"
                isActive={!isScrolling && isSideButtonActive}
                shouldUpdateOpacityWhenInactive={spinButtonState !== SpinButtonState.SEND_IDLE_STATE}
                shouldActAsCollect={areLSAboutToStart}
            />,
            renderSpinButton(),
            renderSpeedButton(),
            <BetOptionButton
                key="betOption"
                isActive={!hasOneBetOption && !isScrolling && isSideButtonActive}
                pulse={
                    !!betGroup &&
                    isEdgeReached &&
                    spinButtonState === SpinButtonState.SEND_IDLE_STATE &&
                    !isMenuOpenRef.current &&
                    !isBetSectionOpened &&
                    !isAutoplaySectionOpened
                }
                isInteractive={isBetOptionsButtonInteractive}
                shouldUpdateOpacityWhenInactive={hasOneBetOption || spinButtonState !== SpinButtonState.SEND_IDLE_STATE}
            />,
        ]
    }

    useCircleProgressAnimation({
        ref: spinButtonAnimationRef,
        duration: minimumSpinTimeDuration,
        isRunning,
        onComplete() {
            setIsRunning(false)
        },
    })

    return (
        <div
            ref={spinButtonSectionComponent}
            className={cn(
                'absolute flex',
                isGambleActive && useRedesignedGamble && spinButtonState === SpinButtonState.SEND_GAMBLE_STATE
                    ? spinButtonGambleSectionConfig.styles
                    : spinButtonSectionConfig.styles
            )}
            style={{
                ...spinButtonPosition,
                ...spinButtonSectionConfig.dimensions,
                display:
                    spinButtonState === SpinButtonState.SEND_HIDE_STATE ||
                    spinButtonState === SpinButtonState.SEND_WAITING_FOR_FREESPIN_ANIMATION_START_STATE
                        ? 'none'
                        : 'flex',
                alignItems: isPortraitGamble ? 'flex-start' : '',
                flexDirection: isPortraitGamble ? 'row-reverse' : undefined,
                zIndex: isMenuOpen ? 0 : 10,
            }}
        >
            {!isCustomResultOpened && <StopButton onClick={handleStopButtonClicked} />}

            {renderAllButtons()}

            {/* Black Overlay */}
            <div
                onClick={() => {
                    close()
                    playPickersCloseSound()
                }}
                className={cn(
                    'fixed w-full h-full bg-black top-[0px] left-[0px] opacity-[0.5] z-[100]',
                    isOverlayOpened ? 'flex' : 'hidden'
                )}
            ></div>
        </div>
    )
}

export default SpinButtonSection
